import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import { getActiveClientSlug } from "./activeClient";

/**
 * Set-for-Campaign picker (AMB-364 / C2). Assigns an asset to a campaign + phase + due date
 * — all optional and all editable later. Available to the submitter or a reviewer at any
 * time; it never blocks approval (a no-campaign asset legitimately ends at "Approved"). On
 * confirm it writes the campaign / phase / due frontmatter (already allowlisted, no
 * migration); with compute-on-read campaigns (AMB-365) the asset then shows in that campaign.
 *
 * Reused by the Reviews drawer and (AMB-367) the My Tasks hub drawer — one component so the
 * action looks and behaves the same everywhere.
 */

interface CampaignOption {
  id: string;
  name: string;
  phases: { id: string; name: string }[];
}

export interface CampaignAssignment {
  campaign: string;
  phase: string | null;
  due: string | null;
}

interface Props {
  /** Current values, so reopening shows what's set (everything is editable later). */
  current?: Partial<CampaignAssignment>;
  onConfirm: (a: CampaignAssignment) => void;
  onClose: () => void;
}

export function SetForCampaignPicker({ current, onConfirm, onClose }: Props) {
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(current?.campaign || "");
  const [phase, setPhase] = useState(current?.phase || "");
  const [due, setDue] = useState(current?.due || "");

  useEffect(() => {
    const slug = getActiveClientSlug();
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) =>
        setCampaigns(
          ((d.items || []) as any[]).map((c) => ({
            id: c.id,
            name: c.name || c.id,
            phases: Array.isArray(c.phases) ? c.phases.map((p: any) => ({ id: p.id, name: p.name || p.id })) : [],
          })),
        ),
      )
      .catch(() => setCampaigns([]))
      .finally(() => setLoading(false));
  }, []);

  const phases = campaigns.find((c) => c.id === campaign)?.phases || [];

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="flex w-full max-w-md flex-col gap-4 rounded-xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-semibold text-gray-900">Add to campaign</h3>

        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Campaign</span>
          <select
            value={campaign}
            onChange={(e) => { setCampaign(e.target.value); setPhase(""); }}
            className="w-full rounded-lg border border-black/[0.08] px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">{loading ? "Loading…" : "Select a campaign…"}</option>
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Phase</span>
          <select
            value={phase}
            onChange={(e) => setPhase(e.target.value)}
            disabled={!campaign || phases.length === 0}
            className="w-full rounded-lg border border-black/[0.08] px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
          >
            <option value="">{!campaign ? "Pick a campaign first" : phases.length === 0 ? "No phases" : "Unphased"}</option>
            {phases.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-gray-400">Due date (optional)</span>
          <input
            type="date"
            value={due ? String(due).slice(0, 10) : ""}
            onChange={(e) => setDue(e.target.value)}
            className="w-full rounded-lg border border-black/[0.08] px-3 py-2 text-[13px] focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <button onClick={onClose} className="rounded-lg px-3 py-2 text-[13px] font-medium text-gray-500 hover:bg-gray-50">Cancel</button>
          <button
            onClick={() => onConfirm({ campaign, phase: phase || null, due: due || null })}
            disabled={!campaign}
            className="rounded-lg bg-gray-900 px-3.5 py-2 text-[13px] font-semibold text-white shadow-sm transition-all hover:bg-gray-800 disabled:opacity-40"
          >
            Add to campaign
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
